import DateFormat from "sap/ui/core/format/DateFormat";
import Type from "./Type";
import ValidateException from "sap/ui/model/ValidateException";
import ParseException from "sap/ui/model/ParseException";
import FormatException from "sap/ui/model/FormatException";

export type MCDateFormatOptions = {
    pattern?: string; // Date format pattern, e.g., "dd/MM/yyyy"
    strictParsing?: boolean; // Whether to enable strict parsing
};
export default class MCDate extends Type {
    private dateInstance: DateFormat;
    private formatOptions: MCDateFormatOptions;
    constructor(formatOptions?: MCDateFormatOptions) {
        super();
        if (formatOptions) {
            this.formatOptions = {
                pattern: formatOptions.pattern || this.getPatternForLocale(), // Default date format
                strictParsing: formatOptions.strictParsing !== undefined ? formatOptions.strictParsing : true, // Enable strict parsing by default
            };
        }
        else {
            this.formatOptions = {
                pattern: this.getPatternForLocale(), // Default date format
                strictParsing: true, // Enable strict parsing by default
            };
        }
        this.dateInstance = DateFormat.getDateInstance(this.formatOptions);
    }

    /**
     * Returns the date format pattern based on the user's locale.
     * @returns {string} The date format pattern.
     */
    private getPatternForLocale(): string {
        switch (this.locale) {
            case "en":
            case "fr":
            case "es":
                return "dd/MM/yyyy"; // Common European format
            case "de":
                return "dd.MM.yyyy"; // German format
            default:
                return "yyyy-MM-dd"; // ISO format as default
        }
    }
    
    private getDateInstance(value: string): Date {
        let day, month, year;
        if (!value) {
            return null;
        }
        if (value.includes('-')) {
            [year, month, day] = value.split('-').map(Number);
        } else if (value.includes('/')) {
            [day, month, year] = value.split('/').map(Number);
        } else if (value.includes('.')) {
            [day, month, year] = value.split('.').map(Number);
        }
        else {
            throw new Error("Invalid date format");
        }
        return new Date(year, month - 1, day); // Month is 0-indexed in JavaScript
    }

    public formatValue(value: Date | string): string {
        if (!value) {
            return "";
        }
        if (typeof value === "string") {
            value = this.getDateInstance(value);
        }
        try {
            return this.dateInstance.format(value);
        } catch {
            throw new FormatException(`Invalid date value: ${value instanceof Date ? value.toISOString() : value}`) as Error;
        }
    }

    public parseValue(value: string | Date): Date {
        if (!value) {
            return null;
        }
        const date = value instanceof Date ? value : this.dateInstance.parse(value);
        if (!date || Array.isArray(date)) {
            throw new ParseException(`Invalid date format: ${value}`) as Error;
        }
        return date as Date;   
    }

    public validateValue(value: string | Date): void {
        if (!value) {
            return;
        }
        const date = value instanceof Date ? value : this.dateInstance.parse(value);
        if (!date || Array.isArray(date)) {
            throw new ValidateException(`Invalid date format: ${value}`) as Error;
        }
    }
}